import { type Database } from "@fitness-app/supabase";

import { type ChallengeActivityCategory } from "./Challenge";

// TRE-1580 — badges (achievements). Definitions are a trainer-editable rule catalog; earned
// badges are idempotent rows (unique per badgeKey × trainee × tier × contextKey) because every
// awarding pipeline can be retried.

export type BadgeDefinitionEntity = Database["public"]["Tables"]["badge_definition"]["Row"];
export type ClientBadgeEntity = Database["public"]["Tables"]["client_badge"]["Row"];

export enum BadgeCategory {
  Workouts = "workouts",
  Nutrition = "nutrition",
  Habits = "habits",
  Challenges = "challenges",
  Activity = "activity",
}

export enum BadgeRuleType {
  /** First day a given activity category appears (config: { category }). */
  FirstEvent = "firstEvent",
  /** Days with the activity category, tiered (config: { category, tiers: number[] }). */
  Counter = "counter",
  /** Habit streak tiers, per client_habit (config: { tiers: number[] }); awarded in SQL. */
  HabitStreak = "habitStreak",
  /** Challenge outcomes (config: { kind: 'thresholdPass' | 'podium' }); awarded by trigger tasks. */
  Challenge = "challenge",
  /**
   * TRE-1590: weight-goal thresholds (bronze/silver/gold). No config — thresholds live on the
   * trainee's client_goal row; awarded in SQL (measurement / client_goal triggers), tier = the
   * 1-based threshold ordinal, contextKey = the goal row id.
   */
  WeightGoal = "weightGoal",
}

export type BadgeChallengeKind = "thresholdPass" | "podium";

/**
 * Badge rules understand every challenge activity category plus `nutritionReports` — the AI
 * nutrition-report feature leaves no ClientDayActivity trace, so badges on it are awarded at the
 * event source (the generate-nutrition-report endpoint), not by the nightly activity sweep.
 */
export type BadgeActivityCategory = ChallengeActivityCategory | "nutritionReports";

/** Typed view over badge_definition.config (shape depends on ruleType). */
export type BadgeConfig = {
  /**
   * firstEvent / counter: which activity/event source feeds the rule. Challenge categories are
   * counted from ClientDayActivity by the sweep; `nutritionReports` is firstEvent-only and
   * awarded at the endpoint (activityCategoryCount always returns 0 for it).
   */
  category?: BadgeActivityCategory;
  /** counter / habitStreak: ascending thresholds; the reached index+1 becomes the tier. */
  tiers?: number[];
  /** challenge: which outcome awards the badge. */
  kind?: BadgeChallengeKind;
  /**
   * challenge scope (both optional; absent = global, awarded for ANY challenge of `kind`):
   *  - challengeId: only that specific challenge instance (one edition),
   *  - challengeTemplateId: every edition of that recurring challenge template.
   * At most one is set; challengeId wins if both somehow present.
   */
  challengeId?: string;
  challengeTemplateId?: string;
  /**
   * TRE-1762 — scoped challenge badges only: whether this badge replaces the global one of the
   * same `kind` inside its scope. Without it a participant crossing the pass mark collects both
   * the generic "Szczyt zdobyty" and the challenge's own badge.
   *
   * Absent counts as ENABLED, so the engine matches the form's default and badges defined before
   * this field behave the way the dedicated badge implies. Ignored on global definitions —
   * there is nothing more specific for them to displace.
   */
  suppressGlobalBadge?: boolean;
} & Record<string, unknown>;

export interface BadgeDefinition extends Omit<BadgeDefinitionEntity, "category" | "ruleType" | "config"> {
  category: BadgeCategory;
  ruleType: BadgeRuleType;
  config: BadgeConfig;
}

export type ClientBadgeContext = {
  clientHabitId?: string;
  challengeId?: string;
  streak?: number;
  count?: number;
  rank?: number;
  /** weightGoal awards: which threshold of which goal was crossed, and at what weight. */
  goalId?: string;
  threshold?: number;
  weight?: number;
  /** The goal's baseline weight — lets the UI show the achieved delta ("−3.2 kg"). */
  startWeight?: number;
  tierName?: "bronze" | "silver" | "gold";
  direction?: "loss" | "gain";
} & Record<string, unknown>;

export interface ClientBadge extends Omit<ClientBadgeEntity, "context"> {
  context: ClientBadgeContext | null;
}

export const asBadgeCategory = (value: string): BadgeCategory =>
  Object.values(BadgeCategory).includes(value as BadgeCategory) ? (value as BadgeCategory) : BadgeCategory.Activity;

export const asBadgeRuleType = (value: string): BadgeRuleType =>
  Object.values(BadgeRuleType).includes(value as BadgeRuleType) ? (value as BadgeRuleType) : BadgeRuleType.Counter;

export const asBadgeConfig = (raw: unknown): BadgeConfig =>
  raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as BadgeConfig) : {};

/** Sorted, sanitized tier thresholds from a config (empty when malformed). */
export const badgeConfigTiers = (config: BadgeConfig): number[] =>
  (Array.isArray(config.tiers) ? config.tiers : [])
    .filter((tier): tier is number => typeof tier === "number" && Number.isFinite(tier) && tier > 0)
    .sort((a, b) => a - b);
